<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMeterSelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'optionSelected']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    path: {
      type: String,
      required: true,
    },
    parameters: {
      type: Object,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    autofocus: {
      type: Boolean,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
    },
    multiple: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_7315092382398562_dataSource = computed(() => ({
    name: 'suggest_meter',
    parameters: props.parameters,
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_7315092382398562_dataSource: b_7315092382398562_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_7315092382398562_dataSource', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_7315092382398562_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.path,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('path');
    }
  );
  watch(
    () => props.parameters,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parameters');
    }
  );
  watch(
    () => props.required,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('required');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.autofocus,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('autofocus');
    }
  );
  watch(
    () => props.clearable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('clearable');
    }
  );
  watch(
    () => props.multiple,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('multiple');
    }
  );

  const act_7315092382398562_script_1c0bex8zc_value_callable = async (
    $event
  ) => {
    return emit('optionSelected', $event.data.option);
  };
  const act_7315092382398562_script_1c0bex8zc_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_1c0bex8zc_value_callable($event);
      },
    };
  };

  const act_7315092382398562_script_1c0bex8zc = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_1c0bex8zc_getActionArgs,
  });
  const inst_evh_7315092382398562_optionSelected_67kv2d7qv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_1c0bex8zc,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_optionSelected_67kv2d7qv],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_optionSelected_67kv2d7qv,
    inst_evh_7315092382398562_optionSelected,
  };

  const evh_7315092382398562_optionSelected_67kv2d7qv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_optionSelected_67kv2d7qv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_optionSelected = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpFormSearchableSelect
    :name="path"
    v-model="item[path]"
    :dataSource="b_7315092382398562_dataSource"
    prefetch
    valueField="id"
    textField="serial_number"
    bind-object
    :multiple="multiple"
    :clearable="clearable"
    :autofocus="autofocus"
    :disabled="readonly || item.isReadonly"
    :readonly="readonly || item.isReadonly"
    :required="required"
    optionTemplate="<UcSuggestMeterOption :option=option></UcSuggestMeterOption>"
    use-option-template-for-selected-option
    v-if="item"
    @optionSelected="evh_7315092382398562_optionSelected($event, {})"
  >
  </ItpFormSearchableSelect>
</template>
